import { useState } from 'react';
import type { Country } from '@/types';
import { FlagImage } from '@/components/flag';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

interface MultipleChoiceProps {
  options: Country[];
  correctCode: string;
  onSelect: (code: string) => void;
  disabled?: boolean;
  showFlags?: boolean;
  selectedCode?: string;
  showResult?: boolean;
}

export function MultipleChoice({
  options,
  correctCode,
  onSelect,
  disabled = false,
  showFlags = false,
  selectedCode,
  showResult = false,
}: MultipleChoiceProps) {
  const [selected, setSelected] = useState<string | null>(selectedCode ?? null);

  const handleSelect = (code: string) => {
    if (disabled || selected) return;
    setSelected(code);
    onSelect(code);
  };

  return (
    <div className={cn('grid gap-2.5', showFlags ? 'grid-cols-2' : 'grid-cols-1')}>
      {options.map((country) => {
        const isSelected = selected === country.code;
        const isCorrect = country.code === correctCode;
        const showCorrect = showResult && isCorrect;
        const showIncorrect = showResult && isSelected && !isCorrect;

        return (
          <button
            key={country.code}
            onClick={() => handleSelect(country.code)}
            disabled={disabled || !!selected}
            className={cn(
              'relative rounded-lg border transition-all duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              'border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900',
              showFlags ? 'p-3' : 'p-3.5 text-left',
              !selected && !disabled && [
                'hover:border-primary-400 hover:bg-primary-50/50 dark:hover:bg-primary-950/20',
                'cursor-pointer',
              ],
              isSelected && !showResult && 'border-primary-500 bg-primary-50 dark:bg-primary-950/30',
              showCorrect && 'border-lime-600 bg-lime-50 dark:bg-lime-950/30',
              showIncorrect && 'border-red-600 bg-red-50 dark:bg-red-950/30',
              (disabled || !!selected) && !showCorrect && !showIncorrect && 'cursor-default opacity-60'
            )}
          >
            {showFlags ? (
              <div className="flex flex-col items-center justify-center gap-2">
                <FlagImage
                  countryCode={country.code}
                  countryName={country.name}
                  size="lg"
                />
                {(showCorrect || showIncorrect) && (
                  <div
                    className={cn(
                      'w-5 h-5 rounded-full flex items-center justify-center',
                      showCorrect && 'bg-lime-600',
                      showIncorrect && 'bg-red-600'
                    )}
                  >
                    {showCorrect && <Check className="w-3 h-3 text-white" />}
                    {showIncorrect && <X className="w-3 h-3 text-white" />}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="font-medium flex-1 text-stone-900 dark:text-stone-100">
                  {country.name}
                </span>
                {showCorrect && (
                  <div className="w-5 h-5 rounded-full bg-lime-600 flex items-center justify-center ml-auto">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                {showIncorrect && (
                  <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center ml-auto">
                    <X className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
